import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/client';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import { FaPlus, FaUserSlash, FaSearch, FaChalkboardTeacher } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function Teachers() {
  const [teachers,      setTeachers]      = useState([]);
  const [grades,        setGrades]        = useState([]);
  const [gradeSubjects, setGradeSubjects] = useState([]); // [{grade_id, grade_number, subject_id, subject_name}]
  const [loading,       setLoading]       = useState(true);
  const [modal,         setModal]         = useState(false);
  const [search,        setSearch]        = useState('');
  const [form,          setForm]          = useState({
    first_name: '', surname: '', email: '', phone: '',
    address: '', qualification: '', employee_number: '', assignments: [],
  });

  const load = async () => {
    try {
      const [teachRes, gradeRes, subjWithGrades] = await Promise.all([
        adminApi.getTeachers(),
        adminApi.getGrades(),
        adminApi.getSubjectsWithGrades(),
      ]);

      const gradeList    = gradeRes.data.data;
      const subjectList  = subjWithGrades.data.data;

      // Build flat list: for each subject, expand its linked grades
      const flat = [];
      for (const subj of subjectList) {
        if (!subj.grades) continue;
        const gradeNumbers = subj.grades.split(', ');
        for (const gradeNum of gradeNumbers) {
          const grade = gradeList.find(g => g.grade_number === gradeNum);
          if (grade) {
            flat.push({
              subject_id:   subj.subject_id,
              subject_name: subj.subject_name,
              subject_code: subj.subject_code,
              grade_id:     grade.grade_id,
              grade_number: grade.grade_number,
              key: `${subj.subject_id}-${grade.grade_id}`,
            });
          }
        }
      }
      // Sort by grade then subject
      flat.sort((a, b) => {
        const gCmp = parseInt(a.grade_number) - parseInt(b.grade_number) || a.grade_number.localeCompare(b.grade_number);
        return gCmp !== 0 ? gCmp : a.subject_name.localeCompare(b.subject_name);
      });

      setTeachers(teachRes.data.data);
      setGrades(gradeList);
      setGradeSubjects(flat);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleAssignment = (item) => {
    const exists = form.assignments.some(a => a.key === item.key);
    if (exists) {
      setForm({ ...form, assignments: form.assignments.filter(a => a.key !== item.key) });
    } else {
      setForm({ ...form, assignments: [...form.assignments, item] });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      assignments: form.assignments.map(a => ({ subject_id: a.subject_id, grade_id: a.grade_id })),
    };
    try {
      await adminApi.addTeacher(payload);
      toast.success('Teacher added! Login credentials sent to their email.');
      setModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this teacher?')) return;
    try { await adminApi.deactivateTeacher(id); toast.success('Teacher deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const openModal = () => {
    setForm({ first_name:'', surname:'', email:'', phone:'', address:'', qualification:'', employee_number:'', assignments:[] });
    setModal(true);
  };

  const filtered = teachers.filter(t =>
    `${t.first_name} ${t.surname} ${t.email}`.toLowerCase().includes(search.toLowerCase())
  );

  // Group grade-subjects by grade for display in modal
  const byGrade = grades.map(g => ({
    ...g,
    subjects: gradeSubjects.filter(gs => gs.grade_id === g.grade_id),
  })).filter(g => g.subjects.length > 0);

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h2>Teachers</h2><p>Manage school teachers and their subject assignments</p></div>
        <button className="sms-btn sms-btn-primary" onClick={openModal}><FaPlus size={13}/> Add Teacher</button>
      </div>

      <div style={{ position:'relative', maxWidth:360, marginBottom:16 }}>
        <FaSearch style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
        <input className="sms-input" style={{ paddingLeft:36 }} placeholder="Search teachers..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="sms-card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <Spinner /> : (
          <table className="sms-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Grades</th><th>Subjects</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.user_id}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:34, height:34, borderRadius:'50%', background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <FaChalkboardTeacher color="#3b82f6" size={14}/>
                      </div>
                      <div>
                        <div style={{ fontWeight:600 }}>{t.first_name} {t.surname}</div>
                        {t.qualification && <div style={{ fontSize:'0.75rem', color:'#9ca3af' }}>{t.qualification}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ color:'#6b7280', fontSize:'0.85rem' }}>{t.email}</td>
                  <td>
                    {t.grades ? t.grades.split(', ').map(g => (
                      <span key={g} style={{ display:'inline-block', background:'#eff6ff', color:'#1d4ed8', fontSize:'0.72rem', fontWeight:700, padding:'2px 7px', borderRadius:10, marginRight:3 }}>Gr {g}</span>
                    )) : <span style={{ color:'#d1d5db' }}>—</span>}
                  </td>
                  <td style={{ fontSize:'0.83rem', color:'#374151', maxWidth:220 }}>
                    {t.subjects || <span style={{ color:'#d1d5db' }}>—</span>}
                  </td>
                  <td><span className={`sms-badge ${t.status==='active'?'badge-present':'badge-absent'}`}>{t.status}</span></td>
                  <td>
                    <button className="sms-btn sms-btn-danger sms-btn-sm" title="Deactivate" onClick={() => deactivate(t.user_id)}>
                      <FaUserSlash size={12}/>
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length===0 && <tr><td colSpan="6" style={{ textAlign:'center', color:'#9ca3af', padding:'40px 0' }}>No teachers found</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <Modal show={modal} onClose={() => setModal(false)} title="Add Teacher" size="lg">
        <form onSubmit={handleSave}>
          {/* Personal details */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
            {[
              ['First Name','first_name',true],
              ['Surname','surname',true],
              ['Email Address','email',true],
              ['Phone','phone',false],
              ['Address','address',false],
              ['Qualification','qualification',false],
              ['Employee Number','employee_number',false],
            ].map(([lbl, key, req]) => (
              <div key={key}>
                <label className="sms-label">{lbl}{req && <span style={{ color:'#ef4444' }}>*</span>}</label>
                <input className="sms-input" type={key==='email'?'email':'text'} value={form[key]}
                  onChange={e => setForm({ ...form, [key]: e.target.value })} required={req} />
              </div>
            ))}
          </div>

          {/* Password notice */}
          <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
            <span style={{ fontSize:'1.2rem' }}>🔐</span>
            <div>
              <div style={{ fontWeight:600, color:'#065f46', fontSize:'0.88rem' }}>Password auto-generated</div>
              <div style={{ color:'#6b7280', fontSize:'0.8rem' }}>A secure password will be generated and emailed to the teacher</div>
            </div>
          </div>

          {/* Subject + Grade assignments */}
          <div>
            <div style={{ marginBottom:10 }}>
              <label className="sms-label" style={{ fontSize:'0.9rem', fontWeight:700 }}>Assign Subjects & Grades</label>
              <p style={{ color:'#6b7280', fontSize:'0.8rem', margin:'3px 0 0' }}>
                A teacher can teach multiple subjects across multiple grades. Check all that apply.
                {form.assignments.length > 0 && <span style={{ color:'#0d6efd', fontWeight:600 }}> ({form.assignments.length} selected)</span>}
              </p>
            </div>

            {gradeSubjects.length === 0 ? (
              <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:8, padding:'12px 16px', fontSize:'0.85rem', color:'#92400e' }}>
                ⚠️ No subjects have been linked to grades yet. Go to <strong>Subjects</strong> and link subjects to grades first.
              </div>
            ) : (
              <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden', maxHeight:320, overflowY:'auto' }}>
                {byGrade.map((grade, gi) => (
                  <div key={grade.grade_id}>
                    {/* Grade header */}
                    <div style={{ padding:'8px 14px', background:'#f8faff', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', gap:8, position:'sticky', top:0, zIndex:1 }}>
                      <div style={{ width:26, height:26, borderRadius:6, background:'#3b82f6', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <span style={{ color:'#fff', fontWeight:700, fontSize:'0.72rem' }}>{grade.grade_number}</span>
                      </div>
                      <span style={{ fontWeight:700, fontSize:'0.85rem', color:'#1d4ed8' }}>Grade {grade.grade_number}</span>
                      {grade.grade_name && <span style={{ color:'#9ca3af', fontSize:'0.78rem' }}>{grade.grade_name}</span>}
                      {/* Select all for this grade */}
                      <button type="button" onClick={() => {
                        const allSelected = grade.subjects.every(s => form.assignments.some(a => a.key === s.key));
                        if (allSelected) {
                          setForm({ ...form, assignments: form.assignments.filter(a => !grade.subjects.some(s => s.key === a.key)) });
                        } else {
                          const toAdd = grade.subjects.filter(s => !form.assignments.some(a => a.key === s.key));
                          setForm({ ...form, assignments: [...form.assignments, ...toAdd] });
                        }
                      }} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', fontSize:'0.75rem', color:'#6b7280', textDecoration:'underline' }}>
                        {grade.subjects.every(s => form.assignments.some(a => a.key === s.key)) ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    {/* Subjects in this grade */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:0 }}>
                      {grade.subjects.map(gs => {
                        const checked = form.assignments.some(a => a.key === gs.key);
                        return (
                          <label key={gs.key} style={{
                            display:'flex', alignItems:'center', gap:9,
                            padding:'9px 14px', cursor:'pointer',
                            background: checked ? '#eff6ff' : '#fff',
                            borderBottom:'1px solid #f3f4f6',
                            transition:'background 0.1s',
                          }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleAssignment(gs)}
                              style={{ width:15, height:15, cursor:'pointer', accentColor:'#3b82f6' }} />
                            <div>
                              <div style={{ fontSize:'0.85rem', fontWeight: checked?600:400, color: checked?'#1d4ed8':'#374151' }}>
                                {gs.subject_name}
                              </div>
                              {gs.subject_code && <div style={{ fontSize:'0.7rem', color:'#9ca3af' }}>{gs.subject_code}</div>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {gi < byGrade.length - 1 && <div style={{ height:1, background:'#e5e7eb' }} />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button type="submit" className="sms-btn sms-btn-primary" style={{ padding:'10px 28px' }}>Add Teacher</button>
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setModal(false)}>Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
